import { useMemo } from 'react';
import { TimeSlotEntry } from '../types/booking';

interface TimeSlotGroup {
	hour: number;
	label: string;
	suffix: string | null;
	slots: TimeSlotEntry[];
}

const createGroupLabel = (date: Date, locale: string, timezone: string) => {
	const groupLabelFormat = new Intl.DateTimeFormat(locale, {
		hour: 'numeric',
		minute: '2-digit',
		timeZone: timezone,
	});
	const startHourDate = new Date(date);
	startHourDate.setMinutes(0, 0, 0);

	const parts = groupLabelFormat.formatToParts(startHourDate);
	const suffix = parts.find((part) => part.type === 'dayPeriod')?.value || null;
	const label = parts
		.filter((part) => part.type !== 'dayPeriod')
		.map((part) => part.value)
		.join('')
		.trim();

	return { label, suffix };
};

export const useTimeSlotGroups = (timeSlots: TimeSlotEntry[], locale: string, timezone: string): TimeSlotGroup[] => {
	const timeSlotGroups = useMemo(() => {
		if (!timeSlots.length) return [];

		// Create a helper to get hour in the specified timezone
		const getHourInTimezone = (date: Date) => {
			const hourFormat = new Intl.DateTimeFormat(locale, {
				hour: 'numeric',
				hour12: false,
				timeZone: timezone,
			});
			return parseInt(hourFormat.format(date));
		};

		// Group slots by hour and build TimeSlotGroup array
		const groupsMap = new Map<number, TimeSlotEntry[]>();

		timeSlots.forEach((slot) => {
			const hour = getHourInTimezone(new Date(slot.start_datetime));
			if (!groupsMap.has(hour)) {
				groupsMap.set(hour, []);
			}
			groupsMap.get(hour)!.push(slot);
		});

		// Convert to TimeSlotGroup array and sort
		return Array.from(groupsMap.entries())
			.map(([hour, slots]) => {
				const { label, suffix } = createGroupLabel(new Date(slots[0].start_datetime), locale, timezone);
				return { hour, label, suffix, slots };
			})
			.sort((a, b) => a.hour - b.hour);
	}, [timeSlots, locale, timezone]);

	return timeSlotGroups;
};
